
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import vSelect from "vue-select";

const SiteMonitor = namespace("siteMonitor");
const Regions = namespace("regions");

import api from "@/services/api.service";
import { ValidateSitePasswordRequest } from "@/store/my-account/types";
import { EventDetails } from "@/store/site-monitor/types";

interface EventAreaDetails {
	address: string;
	armed: boolean;
	canManualRaise: boolean;
	disabledAt: string | null;
	disarmReasonID: number | null;
	groupID: number;
	groupTypeID: number;
	haveKeys: boolean;
	investigatorRequired: boolean;
	isActive: boolean;
	isEncrypted: boolean;
	isSecureLine: boolean;
	keyContacts: KeyConact[];
	lastArmedDisarmedAt: number;
	latLong: string;
	licenseTypeID: number;
	licenseValid: boolean;
	mobileAppEnabled: boolean;
	notes: string;
	onTest: boolean;
	parentGroupID: number;
	responseTime: number;
	status: number;
	telephone: string;
	telephone2: string;
	telephoneFire: string;
	telephonePolice: string;
	timeZoneID: number;
	title: string;
	toggleArmAt: string | null;
	ulType: number;
	ulTypeID: number;
	ulTypeTitle: string | null;
}

interface KeyConact {
	address: string;
	email: string;
	groupID: number;
	isSiteStaff: boolean;
	mobile: string;
	name: string;
	notes: string;
	receiveIncidentEmails: boolean;
	receiveUpdateEmails: boolean;
	role: string;
	sitePassword: string | null;
	telephone: string;
	userID: number;
	username: string;
}

@Component({
	components: {
		"v-select": vSelect
	}
})
export default class AreaDetails extends Vue {
	@SiteMonitor.Mutation setEventDetailsShown: any;

	@Regions.Getter private getRegionUrl: (regionId: number) => string;

	public eventAreaDetails: EventAreaDetails = null;
	public eventUniqueRoles: string[] = ["All Roles"];
	public eventActiveRole: string = "All Roles";

	private modalVisible: boolean = false;

	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;

	@Prop({ type: Boolean, default: false }) showTitle;
	@Prop({ type: Number, default: false }) groupId;
	@Prop({ type: Number, default: 0 }) regionId: number;

	@Watch("groupId")
	private async onGroupIdChanged(newVal, oldVal) {
		if (newVal !== oldVal) {
			await this.loadEventAreaDetails();
		}
	}

	async mounted() {
		await this.loadEventAreaDetails();
	}

	private async loadEventAreaDetails() {
		let regionUrl = this.getRegionUrl(this.regionId);

		this.eventAreaDetails = (await api.loadGroup(this.groupId, regionUrl)) as EventAreaDetails;

		// emit the area details back up to the parent component in Views (saving multiple requests for the same data)
		this.$emit("areaDetails", this.eventAreaDetails);
		this.mapEventUniqueRoles();
	}

	private mapEventUniqueRoles() {
		const roles: string[] = this.eventAreaDetails.keyContacts.map((item: any) => item.role);
		const eventUniqueRoles = new Set(roles);

		const uniqueRoles = Array.from(eventUniqueRoles);
		this.eventUniqueRoles = ["All Roles", ...uniqueRoles];
	}

	private showValidateSitePasswordModal(contact: KeyConact) {
		this.modalVisible = true;
		this.selectedContact = contact;
	}

	private closeModal() {
		this.passwordToValidate = null;
		this.selectedContact = null;
		this.modalVisible = false;
		this.validating = false;
		this.validationResult = null;
	}

	private async performValidation() {
		if (this.validating) {
			return;
		}

		this.validating = true;
		const validateSitePasswordRequest: ValidateSitePasswordRequest = {
			UserId: this.selectedContact.userID,
			EventId: this.eventDetails.eventID,
			SitePassword: this.passwordToValidate
		};

		const passwordIsValid = await api.validateSitePassword(validateSitePasswordRequest);

		this.validating = false;

		this.validationResult = passwordIsValid ? "valid" : "invalid";
	}

	private selectedContact: KeyConact = null;

	private passwordToValidate: string = null;

	private validating: boolean = false;

	private validationResult: string = null;
}
